/**
 * E8AssessmentPanel.js
 * 
 * This component handles the UI for Essential Eight Assessments.
 * Note: createNewAssessment is provided as a prop from the parent component.
 * We should NOT implement a local version to avoid naming conflicts.
 */
import React, { useCallback, useState, useEffect, useRef } from 'react';
import ControlSection from './ControlSection';
import QuickLinks from './QuickLinks/QuickLinks';
import UpdatesPopover from './Updates/UpdatesPopover';
import E8Summary from './EssentialEight/E8Summary';
import { ControlChart } from './EssentialEight/Charts';
import { getCompliancePercentage, calculateControlCompliance } from '../utils/complianceCalculator';
import { PlusIcon, ArrowDownTrayIcon, FolderOpenIcon, CalendarIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import ReactDOM from 'react-dom';
import { toast } from 'react-hot-toast';
import { exportAssessment, importAssessment } from '../utils/assessmentIO';
import AutoSaveIndicator from './AutoSaveIndicator';
import CompactAutoSaveIndicator from './CompactAutoSaveIndicator';

const E8AssessmentPanel = ({
  assessmentName,
  setAssessmentName,
  maturityLevel,
  setMaturityLevel,
  tabs,
  activeTab,
  setActiveTab,
  currentQuestions,
  answers,
  handleAnswerChange,
  getQuestionsForML,
  summaryRecommendations,
  calculateMaturityLevelCompliance,
  saveAssessment,
  loadAssessment,
  createNewAssessment,
  savedAssessments,
  setSavedAssessments,
  assessmentDate,
  handleDateChange,
  fileInputRef,
  deleteAssessment,
  autoSave,
  hasManualSave
}) => {
  const [showAssessmentManager, setShowAssessmentManager] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [pendingAssessmentName, setPendingAssessmentName] = useState(null);
  const [dialogAction, setDialogAction] = useState(null);
  
  // Add state for collapsible panel and active tab
  const [isPanelExpanded, setIsPanelExpanded] = useState(false);
  const [activePanelTab, setActivePanelTab] = useState('overview');
  
  // Debug useEffect to log when fileInputRef changes
  useEffect(() => {
    console.log('E8AssessmentPanel received fileInputRef:', fileInputRef);
  }, [fileInputRef]);

  // Add debug effect for auto-save
  useEffect(() => {
    if (autoSave) {
      console.log('E8AssessmentPanel auto-save state:', {
        isEnabled: autoSave.isAutoSaveEnabled,
        status: autoSave.autoSaveStatus,
        countdown: autoSave.nextSaveCountdown,
        lastSave: autoSave.lastSaveTime
      });
    }
  }, [autoSave?.isAutoSaveEnabled, autoSave?.autoSaveStatus, autoSave?.nextSaveCountdown, autoSave?.lastSaveTime]);

  // Add debug effect for maturity level and currentQuestions
  useEffect(() => {
    console.log('Maturity level changed:', maturityLevel);
    console.log('Active tab:', activeTab);
    
    if (maturityLevel && tabs && tabs[activeTab]) {
      console.log('Current control:', tabs[activeTab].name);
      // Log first few questions we should be showing
      if (currentQuestions && currentQuestions.length > 0) {
        console.log('Questions available:', currentQuestions.length);
        console.log('First few questions:', currentQuestions.slice(0, 2));
      } else {
        console.warn('No questions available for current control and maturity level');
      }
    }
  }, [maturityLevel, activeTab, currentQuestions, tabs]);

  // Calculate control compliance for a specific control and maturity level
  const getControlCompliance = useCallback((controlName, ml) => {
    const questions = getQuestionsForML(controlName, ml);
    return calculateControlCompliance(answers, questions);
  }, [getQuestionsForML, answers]);

  // Get number of answered questions for the current control
  const getAnsweredCount = useCallback((controlName) => {
    let count = 0;
    const levels = ['ML1', 'ML2', 'ML3'];
    const selectedLevelIndex = levels.indexOf(maturityLevel);

    for (let i = 0; i <= selectedLevelIndex; i++) {
      const ml = levels[i];
      const questionsForML = getQuestionsForML(controlName, ml);
      count += questionsForML.filter(q => answers[q.id] && answers[q.id].value !== '').length;
    }

    return count;
  }, [maturityLevel, getQuestionsForML, answers]);

  // Get total number of questions for the current control
  const getTotalQuestionsCount = useCallback((controlName) => {
    let count = 0;
    const levels = ['ML1', 'ML2', 'ML3'];
    const selectedLevelIndex = levels.indexOf(maturityLevel);

    for (let i = 0; i <= selectedLevelIndex; i++) {
      const ml = levels[i];
      count += getQuestionsForML(controlName, ml).length;
    }

    return count;
  }, [maturityLevel, getQuestionsForML]);

  // Calculate progress percentage
  const getProgressPercentage = useCallback(() => {
    if (!tabs[activeTab]) return 0;
    const answeredCount = getAnsweredCount(tabs[activeTab].name);
    const totalCount = getTotalQuestionsCount(tabs[activeTab].name);
    return totalCount > 0 ? (answeredCount / totalCount) * 100 : 0;
  }, [activeTab, tabs, getAnsweredCount, getTotalQuestionsCount]);

  // Get overall progress across all controls
  const getOverallProgress = useCallback(() => {
    if (!tabs || tabs.length === 0 || !maturityLevel) return { completed: 0, total: 0, percentage: 0 };
    
    let totalAnswered = 0;
    let totalQuestions = 0;
    
    tabs.forEach(tab => {
      if (tab && tab.name) {
        const answered = getAnsweredCount(tab.name);
        const total = getTotalQuestionsCount(tab.name);
        
        totalAnswered += answered;
        totalQuestions += total;
      }
    });
    
    return {
      completed: totalAnswered,
      total: totalQuestions,
      percentage: totalQuestions > 0 ? (totalAnswered / totalQuestions) * 100 : 0
    };
  }, [tabs, maturityLevel, getAnsweredCount, getTotalQuestionsCount]);

  // Control descriptions for each maturity level
  const controlDescriptions = {
    'Patch Applications': {
      ML1: 'Patches/mitigates extreme risk vulnerabilities within 48 hours.',
      ML2: 'Patches/mitigates high or critical risk vulnerabilities within two weeks.',
      ML3: 'Patches/mitigates medium risk vulnerabilities within one month.'
    },
    'Patch Operating Systems': {
      ML1: 'Patches critical vulnerabilities in 48 hours, others in 2-4 weeks.',
      ML2: 'Maintains patching standards established in ML1.',
      ML3: 'Extends patching to drivers and firmware, uses latest OS versions.'
    },
    'Multi-factor Authentication': {
      ML1: 'Implements MFA for remote access and important data/actions.',
      ML2: 'Extends MFA to all users, uses phishing-resistant methods.',
      ML3: 'Implements phishing-resistant MFA for all users and data repositories.'
    },
    'Restrict Administrative Privileges': {
      ML1: 'Validates privilege requests, uses separate admin accounts.',
      ML2: 'Implements time-based access, auditing, and jump servers.',
      ML3: 'Uses Just-In-Time administration and secure admin workstations.'
    },
    'Application Control': {
      ML1: 'Restricts execution on workstations to an approved set.',
      ML2: 'Extends control to internet-facing servers and all disk locations.',
      ML3: 'Includes driver execution restriction and vulnerable driver blocklist.'
    },
    'Configure Microsoft Office macro settings': {
      ML1: 'Microsoft Office macros are disabled for users that do not have a demonstrated business requirement.',
      ML2: 'Microsoft Office macros in files originating from the internet are blocked.',
      ML3: 'Microsoft Office macro antivirus scanning is enabled.'
    },
    'User Application Hardening': {
      ML1: 'Internet Explorer 11 is disabled or removed.',
      ML2: 'Web browsers do not process Java from the internet.',
      ML3: 'Web browsers do not process Java from the internet.'
    },
    'Regular Backups': {
      ML1: 'Performs regular backups, restricts access, tests annually.',
      ML2: 'Prevents modification/deletion by unprivileged accounts.',
      ML3: 'Further restricts backup access and modification rights.'
    }
  };

  // Get description for the current control
  const getControlDescription = useCallback((controlName) => {
    if (!controlDescriptions[controlName]) return "Description not available.";
    
    let description = '';
    if (maturityLevel === 'ML1' || maturityLevel === 'ML2' || maturityLevel === 'ML3') {
      description += `ML1: ${controlDescriptions[controlName].ML1}\n`;
    }
    if (maturityLevel === 'ML2' || maturityLevel === 'ML3') {
      description += `ML2: ${controlDescriptions[controlName].ML2}\n`;
    }
    if (maturityLevel === 'ML3') {
      description += `ML3: ${controlDescriptions[controlName].ML3}`;
    }
    return description.trim();
  }, [maturityLevel]);

  // Render control chart with memoization
  const renderControlChart = useCallback((ml) => {
    let compliancePercentage = 0;
    const currentControl = tabs[activeTab]?.name;
    const isPatchOS = currentControl === 'Patch Operating Systems';
    
    // For Patch Operating Systems:
    // - ML1: Show actual ML1 compliance
    // - ML2: Auto-complete when ML1 is complete
    // - ML3: Show actual ML3 compliance when ML1 is complete
    if (isPatchOS) {
      const ml1Compliance = getControlCompliance(currentControl, 'ML1');
      
      if (ml === 'ML1') {
        compliancePercentage = ml1Compliance;
      } else if (ml === 'ML2') {
        compliancePercentage = ml1Compliance; // ML2 mirrors ML1 for Patch OS
      } else if (ml === 'ML3') {
        // For ML3, directly check if all ML3 questions are effective
        const ml3Questions = getQuestionsForML(currentControl, 'ML3');
        const allML3Effective = ml3Questions.every(q => answers[q.id]?.value === 'effective');
        compliancePercentage = allML3Effective ? 100 : 0;
      }
    } else {
      // Original logic for other controls
      if (ml === 'ML1') {
        compliancePercentage = getControlCompliance(currentControl, 'ML1');
      } else if (ml === 'ML2') {
        const ml1Compliance = getControlCompliance(currentControl, 'ML1');
        compliancePercentage = ml1Compliance === 100 ? getControlCompliance(currentControl, 'ML2') : 0;
      } else if (ml === 'ML3') {
        const ml1Compliance = getControlCompliance(currentControl, 'ML1');
        const ml2Compliance = getControlCompliance(currentControl, 'ML2');
        compliancePercentage = (ml1Compliance === 100 && ml2Compliance === 100) ? 
          getControlCompliance(currentControl, 'ML3') : 0;
      }
    }

    return <ControlChart key={ml} ml={ml} compliancePercentage={compliancePercentage} />;
  }, [activeTab, tabs, getControlCompliance, getQuestionsForML, answers]);

  // Format date for display
  const formatDate = (date) => {
    if (!date) return new Date().toLocaleDateString('en-AU');
    return new Date(date).toLocaleDateString('en-AU');
  };

  // Wrapper for handleAnswerChange to handle different parameter formats
  const onAnswerChange = (questionId, value, field, fieldValue) => {
    if (field && fieldValue !== undefined) {
      // If we have both field and fieldValue (including empty string), update the specific field
      handleAnswerChange(questionId, fieldValue, field);
    } else {
      // Otherwise just update the main value
      handleAnswerChange(questionId, value);
    }
  };

  // Custom confirmation dialog component with support for different dialog types
  const ConfirmationDialog = ({ message, onConfirm, onCancel }) => {
    const isNotice = dialogAction === 'notice';
    const isError = dialogAction === 'error';
    const isConfirmation = !isNotice && !isError;
    
    return ReactDOM.createPortal(
      <div className="fixed inset-0 z-[9999] overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
          <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
            <div>
              {isError && (
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                  <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
              )}
              <div className="mt-3 text-center sm:mt-0 sm:text-left">
                <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                  {isNotice ? 'Notice' : isError ? 'Error' : 'Confirm Action'}
                </h3>
                <div className="mt-2">
                  <p className={`text-sm ${isError ? 'text-red-600' : 'text-gray-500'}`}>
                    {message}
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 ${
                  isError ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500' : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
                } text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm`}
                onClick={!isConfirmation ? () => setShowConfirmDialog(false) : onConfirm}
              >
                {isConfirmation ? 'OK' : 'Close'}
              </button>
              {isConfirmation && (
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                  onClick={onCancel}
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  // Safely stringify an object by removing problematic properties
  const safelyStringifyObject = (obj) => {
    // Create a completely new object with only primitive values
    const sanitizeObject = (value, depth = 0, maxDepth = 10) => {
      // Prevent infinite recursion
      if (depth > maxDepth) return "[Max Depth Exceeded]";
      
      // Handle null/undefined
      if (value === null || value === undefined) return value;
      
      // Return primitive values directly
      if (typeof value !== 'object') return value;
      
      // Handle arrays
      if (Array.isArray(value)) {
        return value.map(item => sanitizeObject(item, depth + 1, maxDepth));
      }
      
      // Skip problematic objects entirely
      if (
        // DOM nodes
        (typeof window !== 'undefined' && value instanceof Node) ||
        // React elements typically have these properties
        value.$$typeof || 
        value._owner || 
        value.props ||
        // React fiber nodes
        value._reactInternalFiber ||
        value.__reactFiber ||
        value.stateNode
      ) {
        return "[React Element]";
      }
      
      // For regular objects, create a new clean object
      const newObj = {};
      for (const key in value) {
        // Skip problematic keys and functions
        if (
          !Object.prototype.hasOwnProperty.call(value, key) ||
          typeof value[key] === 'function' ||
          key.startsWith('_react') ||
          key.startsWith('__react') ||
          key === 'ref' ||
          key === 'key'
        ) {
          continue;
        }
        
        try {
          newObj[key] = sanitizeObject(value[key], depth + 1, maxDepth);
        } catch (err) {
          // If any property causes an error, skip it
          newObj[key] = "[Error: " + err.message + "]";
        }
      }
      
      return newObj;
    };
    
    try {
      // First create a sanitized version of the object
      const sanitized = sanitizeObject(obj);
      
      // Then try to stringify it
      return JSON.stringify(sanitized);
    } catch (error) {
      console.error("Failed to stringify object:", error);
      return null;
    }
  };

  // Handle save assessment with confirmation for existing assessments
  const handleSaveAssessment = (assessmentName, notify = true) => {
    console.log('🟢 === GREEN SAVE BUTTON CLICKED ===');
    console.log('📋 Input parameters:', { assessmentName, notify });
    console.log('📋 Current state before save:', {
      assessmentName: assessmentName,
      maturityLevel,
      hasManualSave,
      savedAssessments: savedAssessments.length
    });
    
    if (!assessmentName || assessmentName.trim() === '') {
      console.log('❌ Save failed: Assessment name is empty');
      toast.error('Assessment name cannot be empty');
      return;
    }
    
    if (!maturityLevel || !['ML1', 'ML2', 'ML3'].includes(maturityLevel)) {
      console.log('❌ Save failed: Invalid maturity level:', maturityLevel);
      toast.error('Invalid maturity level');
      return;
    }
    
    console.log('✅ Validation passed, proceeding with save...');
    
    // Check for existing assessment with the same name
    const existingIndex = savedAssessments.findIndex(a => a.name === assessmentName);
    console.log('🔍 Checking for existing assessment:', { existingIndex, assessmentName });
    
    if (existingIndex !== -1) {
      console.log('⚠️ Assessment exists, showing confirmation dialog');
      setDialogMessage(`An assessment named "${assessmentName}" already exists. Do you want to overwrite it?`);
      setPendingAssessmentName(assessmentName);
      setDialogAction('save');
      setShowConfirmDialog(true);
    } else {
      console.log('✅ New assessment, calling save function...');
      try {
        // Use the parent component's saveAssessment function rather than our local one
        if (saveAssessment) {
          console.log('📞 Calling parent saveAssessment function...');
          saveAssessment(
            assessmentName,
            (successMessage) => {
              console.log('✅ Save SUCCESS callback:', successMessage);
              if (notify) {
                toast.success(successMessage || 'Assessment saved successfully');
              }
              setDialogMessage(successMessage || 'Assessment saved successfully');
              setDialogAction('notice');
              setShowConfirmDialog(true);
            },
            (errorMessage) => {
              console.log('❌ Save ERROR callback:', errorMessage);
              console.error("Save error:", errorMessage);
              toast.error(`Error saving assessment: ${errorMessage}`);
              setDialogMessage(`There was an error saving the assessment: ${errorMessage}`);
              setDialogAction('error');
              setShowConfirmDialog(true);
            }
          );
        } else {
          toast.error('Server assessment persistence is unavailable');
        }
      } catch (error) {
        console.log('❌ Exception in handleSaveAssessment:', error);
        console.error("Exception in handleSaveAssessment:", error);
        toast.error(`Error: ${error.message}`);
        setDialogMessage(`Exception in handleSaveAssessment: ${error.message}`);
        setDialogAction('error');
        setShowConfirmDialog(true);
      }
    }
    
    console.log('🔚 handleSaveAssessment function completed');
  };

  // Add a ref for the assessment manager dropdown
  const assessmentManagerRef = useRef(null);
  
  // Add a click away listener to close the assessment manager dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (assessmentManagerRef.current && !assessmentManagerRef.current.contains(event.target)) {
        setShowAssessmentManager(false);
      }
    }
    
    // Add event listener if dropdown is open
    if (showAssessmentManager) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    // Clean up
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAssessmentManager]);

  // Add a function to handle assessment deletion
  const handleDeleteAssessment = (assessmentName, event) => {
    // Stop propagation to prevent triggering the parent button click
    event.stopPropagation();
    
    // Confirm before deleting
    setDialogMessage(`Are you sure you want to delete "${assessmentName}"? This cannot be undone.`);
    setPendingAssessmentName(assessmentName);
    setDialogAction('delete');
    setShowConfirmDialog(true);
  };

  // Handle confirmation result with improved error handling
  const handleConfirmAction = () => {
    try {
      // Close the dialog first in all cases
      setShowConfirmDialog(false);
      
      // Then perform the appropriate action
      if (dialogAction === 'save' && pendingAssessmentName) {
        try {
          // Use the parent component's saveAssessment function rather than our local one
          if (saveAssessment) {
            saveAssessment(
              pendingAssessmentName,
              (successMsg) => toast.success(successMsg || "Assessment saved successfully"),
              (errorMsg) => toast.error(`Error: ${errorMsg}`)
            );
          } else {
            toast.error('Server assessment persistence is unavailable');
          }
        } catch (error) {
          console.error("Save error:", error);
          toast.error(`Failed to save: ${error.message}`);
        }
      } else if (dialogAction === 'new') {
        // Call the createNewAssessment function from props and ensure it works first time
        if (typeof createNewAssessment === 'function') {
          // Make sure we force a reset of all state
          createNewAssessment();
          
          // Force a refresh of the UI after a short delay
          setTimeout(() => {
            // Ensure maturity level is set
            if (!maturityLevel) {
              setMaturityLevel('ML1');
            }
            // Force a refresh of the questions
            if (typeof setActiveTab === 'function') {
              setActiveTab(0);
            }
          }, 50);
          
          toast.success("Created new assessment");
        }
      } else if (dialogAction === 'delete' && pendingAssessmentName) {
        try {
          console.log("Deleting assessment:", pendingAssessmentName);
          
          // First, make sure the dropdown closes
          setShowAssessmentManager(false);
          
          // If we're deleting the current assessment, create a new one
          if (assessmentName === pendingAssessmentName) {
            if (typeof createNewAssessment === 'function') {
              createNewAssessment();
            }
          }
          
          // Use the hook's deleteAssessment function if available
          if (typeof deleteAssessment === 'function') {
            // The parent's deleteAssessment function handles everything
            deleteAssessment(pendingAssessmentName);
            toast.success(`Assessment "${pendingAssessmentName}" deleted successfully`);
            return;
          }
          
          throw new Error('Server assessment deletion is unavailable');
        } catch (error) {
          console.error("Delete error:", error);
          toast.error(`Failed to delete: ${error.message}`);
        }
      }
    } catch (error) {
      console.error("Error in handleConfirmAction:", error);
      toast.error(`An error occurred: ${error.message}`);
    }
  };

  // Handle new assessment with confirmation if needed
  const handleNewAssessment = () => {
    if (Object.keys(answers).length > 0) {
      setDialogMessage("Creating a new assessment will clear current data. Do you want to continue?");
      setDialogAction('new');
      setShowConfirmDialog(true);
    } else {
      // Call the createNewAssessment function from props
      if (typeof createNewAssessment === 'function') {
        createNewAssessment();
        
        // Force a refresh of the UI after a short delay
        setTimeout(() => {
          // Ensure maturity level is set
          if (!maturityLevel) {
            setMaturityLevel('ML1');
          }
          // Force a refresh of the questions
          if (typeof setActiveTab === 'function') {
            setActiveTab(0);
          }
        }, 50);
      }
    }
  };

  // Handle exporting the current assessment
  const handleExportAssessment = () => {
    try {
      // Validate that we have a proper assessment to export
      if (!assessmentName || assessmentName.trim() === '') {
        toast.error('Please provide an assessment name before exporting');
        return;
      }

      // Create a clean assessment object for export
      const assessmentToExport = {
        name: assessmentName,
        maturityLevel,
        tabs,
        answers,
        activeTab,
        created: new Date().toISOString(),
        assessmentDate
      };

      // Use the utility function to export
      const success = exportAssessment(assessmentToExport);
      
      if (success) {
        toast.success('Assessment exported successfully');
      } else {
        toast.error('Failed to export assessment');
      }
    } catch (error) {
      console.error('Error exporting assessment:', error);
      toast.error(`Export failed: ${error.message}`);
    }
  };

  // Handle importing an assessment from a file
  const handleImportAssessment = async (event) => {
    try {
      // Make sure we have a file to import
      if (!event || !event.target || !event.target.files || !event.target.files[0]) {
        console.error("No file selected or event is invalid");
        toast.error("Please select a valid file to import");
        return;
      }

      const file = event.target.files[0];
      console.log("Importing file:", file.name);
      
      // Start loading indicator
      toast.loading('Importing assessment...', { id: 'import-toast' });

      try {
        // Import the assessment using the utility function
        const importedAssessment = await importAssessment(file);
        console.log("Assessment imported:", importedAssessment?.name);

        // If we have a valid assessment, proceed to load it
        if (importedAssessment) {
          // Check if an assessment with this name already exists
          const exists = savedAssessments.some(a => a.name === importedAssessment.name);
          
          if (exists) {
            // Add "(Imported)" to the name to avoid conflicts
            importedAssessment.name = `${importedAssessment.name} (Imported)`;
          }

          // Use loadAssessment to properly load the imported data
          loadAssessment(importedAssessment);
          
          // Show success message
          toast.success('Assessment imported successfully', { id: 'import-toast' });
        } else {
          toast.error('Invalid assessment file format', { id: 'import-toast' });
        }
      } catch (importError) {
        console.error("Error importing assessment:", importError);
        toast.error(`Import failed: ${importError.message}`, { id: 'import-toast' });
      } finally {
        // Reset the file input
        if (fileInputRef && fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    } catch (error) {
      console.error("Exception in handleImportAssessment:", error);
      toast.error(`Import failed: ${error.message}`);
      
      // Ensure the loading toast is dismissed
      toast.dismiss('import-toast');
    }
  };

  // Add event listener for auto-save testing
  useEffect(() => {
    const handleAutoSaveTest = (event) => {
      console.log('Auto-save test triggered:', event.detail);
      // Make a small change to trigger auto-save
      const newDate = new Date().toISOString().split('T')[0];
      handleDateChange(newDate);
    };

    const handleDebugAssessmentState = () => {
      console.log('=== CURRENT ASSESSMENT STATE ===');
      console.log('assessmentName:', assessmentName);
      console.log('maturityLevel:', maturityLevel);
      console.log('assessmentDate:', assessmentDate);
      console.log('answersCount:', Object.keys(answers).length);
      console.log('activeTab:', activeTab);
      console.log('savedAssessments count:', savedAssessments.length);
      console.log('================================');
    };

    window.addEventListener('triggerAutoSaveTest', handleAutoSaveTest);
    window.addEventListener('debugAssessmentState', handleDebugAssessmentState);
    
    return () => {
      window.removeEventListener('triggerAutoSaveTest', handleAutoSaveTest);
      window.removeEventListener('debugAssessmentState', handleDebugAssessmentState);
    };
  }, [handleDateChange, assessmentName, maturityLevel, assessmentDate, answers, activeTab, savedAssessments]);

  // FR-3.5: Reset to overview tab when assessment loads
  useEffect(() => {
    setActivePanelTab('overview');
  }, [assessmentName, maturityLevel]);

  return (
    <div className="flex flex-col h-full">
      {/* Hidden file input for importing assessments */}
      <input
        type="file"
        ref={fileInputRef}
        accept=".json"
        className="hidden"
        onChange={handleImportAssessment}
      />

      <div className="border-t border-b border-gray-200 shadow-lg bg-white mb-4">
          {/* Current Strategy/Control Banner */}
          {tabs && tabs[activeTab] && maturityLevel && (
            <div 
              className={`text-white py-3 px-6 font-bold flex justify-between items-center relative overflow-hidden transition-all duration-1000 ${
                autoSave?.autoSaveStatus === 'saving' 
                  ? 'bg-gradient-to-r from-blue-400 via-blue-600 to-blue-600' 
                  : autoSave?.autoSaveStatus === 'success'
                  ? 'bg-gradient-to-r from-green-500 via-blue-600 to-blue-600'
                  : autoSave?.autoSaveStatus === 'error'
                  ? 'bg-gradient-to-r from-red-500 via-blue-600 to-blue-600'
                  : 'bg-blue-600'
              }`}
            >
              {/* Progress Bar for Auto-Save */}
              {autoSave?.autoSaveStatus === 'saving' && (
                <div className="absolute inset-0 bg-gradient-to-r from-blue-300/30 to-transparent animate-pulse"></div>
              )}
              
              {/* Control Name and Maturity Level */}
              <h2 className="text-xl md:text-2xl flex items-center relative z-10">
                {tabs[activeTab].name}
                <span className="ml-3 px-2 py-1 bg-blue-800 text-sm rounded-md">{maturityLevel}</span>
              </h2>
              
              {/* Auto-Save Indicator and Navigation */}
              <div className="flex items-center space-x-4 relative z-10">
                {/* Auto-Save Indicator */}
                {autoSave && (
                  <CompactAutoSaveIndicator
                    isAutoSaveEnabled={autoSave.isAutoSaveEnabled}
                    autoSaveStatus={autoSave.autoSaveStatus}
                    lastSaveTime={autoSave.lastSaveTime}
                    nextSaveCountdown={autoSave.nextSaveCountdown}
                    toggleAutoSave={autoSave.toggleAutoSave}
                    saveNow={autoSave.saveNow}
                    hasManualSave={hasManualSave}
                  />
                )}
                
                {/* Navigation Buttons */}
                <div className="flex space-x-2">
                  <button
                    onClick={() => setActiveTab(prevTab => Math.max(0, prevTab - 1))}
                    disabled={activeTab === 0}
                    className={`p-1 rounded ${activeTab === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'}`}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                  <button 
                    onClick={() => setActiveTab(prevTab => Math.min(tabs.length - 1, prevTab + 1))}
                    disabled={activeTab === tabs.length - 1}
                    className={`p-1 rounded ${activeTab === tabs.length - 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'}`}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Collapsible Assessment Control Panel */}
          <div className="border-t border-gray-100">
            {/* FR-1: Header Consolidation - Always Visible */}
            <div 
              className="w-full px-6 py-3 bg-gray-50 hover:bg-gray-100 transition-colors duration-150 cursor-pointer"
              onClick={() => setIsPanelExpanded(!isPanelExpanded)}
            >
              <div className="flex items-center justify-between">
                {/* FR-1.1 & FR-1.2: Left Section - Assessment Name & Maturity Level */}
                <div className="flex items-center space-x-4 flex-1">
                <span className="font-medium text-gray-700">Assessment Control Panel</span>
                  
                  {/* FR-1.1: Assessment Name Field */}
                  <div className="relative">
                          <input
                            type="text"
                            value={assessmentName || ''}
                            onChange={(e) => setAssessmentName(e.target.value)}
                      placeholder="Assessment name"
                      readOnly={hasManualSave}
                      onClick={(e) => e.stopPropagation()}
                      className={`border rounded px-2 py-1 text-sm min-w-[200px] ${
                        hasManualSave 
                          ? 'bg-gray-100 text-gray-500 cursor-not-allowed border-gray-300' 
                          : 'border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500'
                      }`}
                      title={hasManualSave ? "Assessment name cannot be changed after first save" : ""}
                          />
                        </div>
                        
                  {/* FR-1.2: Maturity Level Dropdown */}
                          <select
                    value={maturityLevel || 'ML1'}
                            onChange={(e) => setMaturityLevel(e.target.value)}
                    className="border border-gray-300 rounded px-2 py-1 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    onClick={(e) => e.stopPropagation()}
                          >
                            <option value="ML1">ML1</option>
                            <option value="ML2">ML2</option>
                            <option value="ML3">ML3</option>
                          </select>
                        </div>

                {/* FR-1.3: Right Section - Action Buttons + Status + Expand */}
                <div className="flex items-center space-x-2">
                  {/* FR-4: Action Button Consolidation */}
                        <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNewAssessment();
                    }}
                    className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors"
                    title="Create new assessment"
                  >
                          New
                        </button>
                  
                        <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSaveAssessment(assessmentName);
                    }}
                    className="px-3 py-1 bg-blue-700 text-white rounded text-sm hover:bg-blue-800 transition-colors"
                          disabled={!assessmentName}
                    title="Save assessment"
                        >
                          Save
                        </button>
                        
                        <div className="relative">
                          <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowAssessmentManager(!showAssessmentManager);
                      }}
                      className="px-3 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-700 transition-colors"
                      title="Load assessment"
                    >
                            Load
                          </button>
                          
                    {/* Keep existing Assessment Manager Dropdown */}
                          {showAssessmentManager && (
                            <div 
                              ref={assessmentManagerRef}
                              className="absolute right-0 mt-2 w-72 bg-white rounded-md shadow-lg z-50 overflow-hidden"
                            >
                              <div className="py-1">
                                <div className="px-4 py-2 text-sm font-medium text-gray-900 bg-gray-100">
                                  Saved Assessments
                                </div>
                                <div className="max-h-60 overflow-y-auto">
                                  {savedAssessments.length === 0 ? (
                                    <div className="px-4 py-3 text-sm text-gray-700">
                                      No saved assessments found.
                                    </div>
                                  ) : (
                                    savedAssessments.map((assessment, index) => (
                                      <div key={index} className="flex items-center border-b border-gray-100 last:border-0">
                                        <button
                                          onClick={() => {
                                            loadAssessment(assessment);
                                            setShowAssessmentManager(false);
                                          }}
                                          className="flex-1 text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                        >
                                          <div className="font-medium">{assessment.name}</div>
                                          <div className="text-xs text-gray-500 mt-1 space-y-1">
                                            <div>Assessment: {assessment.assessmentDate ? formatDate(assessment.assessmentDate) : 'No date'}</div>
                                            <div className="text-green-600">
                                              Last saved: {assessment.lastSaved ? new Date(assessment.lastSaved).toLocaleString('en-AU', {
                                                day: '2-digit',
                                                month: '2-digit', 
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                              }) : 'Unknown'}
                                            </div>
                                          </div>
                                        </button>
                                        <button
                                          onClick={(e) => handleDeleteAssessment(assessment.name, e)}
                                          className="p-2 text-red-600 hover:bg-red-50 rounded-full mr-2"
                                          title="Delete assessment"
                                        >
                                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                          </svg>
                                        </button>
                                      </div>
                                    ))
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleExportAssessment();
                    }}
                    className="px-3 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-700 transition-colors"
                          disabled={!assessmentName}
                    title="Export assessment"
                        >
                          Export
                        </button>

                        <button
                    onClick={(e) => {
                      e.stopPropagation();
                            if (fileInputRef && fileInputRef.current) {
                              fileInputRef.current.click();
                            }
                          }}
                    className="px-3 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-700 transition-colors"
                    title="Import assessment"
                        >
                          Import
                        </button>

                  {/* Quick status indicators when collapsed */}
                  {!isPanelExpanded && (
                    <div className="flex items-center space-x-2 text-sm text-gray-500 ml-4">
                      <span>{getAnsweredCount(tabs[activeTab]?.name)}/{getTotalQuestionsCount(tabs[activeTab]?.name)}</span>
                      <div className="w-8 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-blue-500 rounded-full transition-all duration-300"
                          style={{ width: `${getProgressPercentage()}%` }}
                        ></div>
                      </div>
                    </div>
                  )}

                  {/* Expand/Collapse Chevron */}
                  <svg 
                    className={`w-5 h-5 transition-transform duration-200 ${isPanelExpanded ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Expandable Panel Content */}
            {isPanelExpanded && (
              <div className="border-t border-gray-200">
                {/* Tab Navigation */}
                <div className="flex border-b border-gray-200 bg-white">
                  <button
                    onClick={() => setActivePanelTab('overview')}
                    className={`px-6 py-3 text-sm font-medium transition-colors duration-200 border-b-2 ${
                      activePanelTab === 'overview'
                        ? 'border-blue-500 text-blue-600 bg-blue-50'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    🏛️ Overview
                  </button>
                  <button
                    onClick={() => setActivePanelTab('progress')}
                    className={`px-6 py-3 text-sm font-medium transition-colors duration-200 border-b-2 ${
                      activePanelTab === 'progress'
                        ? 'border-blue-500 text-blue-600 bg-blue-50'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    📊 Progress
                  </button>
                </div>

                {/* Tab Content */}
                <div className="p-6 bg-white">
                  {/* Overview Tab - Essential Eight Controls Overview (Renamed from Summary) */}
                  {activePanelTab === 'overview' && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-medium text-gray-700">Essential Eight Controls Overview</h3>
                        <div className="flex items-center space-x-2 text-sm text-gray-500">
                          <span>Navigate:</span>
                          <button 
                            onClick={() => setActiveTab(prevTab => Math.max(0, prevTab - 1))}
                            disabled={activeTab === 0}
                            className={`p-1 rounded-md ${activeTab === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-blue-100'}`}
                            title="Previous control"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </button>
                          <button 
                            onClick={() => setActiveTab(prevTab => Math.min(tabs.length - 1, prevTab + 1))}
                            disabled={activeTab === tabs.length - 1}
                            className={`p-1 rounded-md ${activeTab === tabs.length - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-blue-100'}`}
                            title="Next control"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <E8Summary 
                        controls={tabs}
                        currentMaturityLevels={{
                          activeTab,
                          maturityLevel
                        }}
                        onControlClick={setActiveTab}
                        answers={answers}
                        getQuestionsForML={getQuestionsForML}
                        getCompliancePercentage={getCompliancePercentage}
                      />
                    </div>
                  )}

                  {/* Progress Tab */}
                  {activePanelTab === 'progress' && (
                    <div className="space-y-6">
                      {/* Progress Indicators */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-3">Control Progress</h4>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-gray-600">Questions Answered</span>
                            <span className="text-sm font-semibold text-blue-600">
                              {getAnsweredCount(tabs[activeTab]?.name)} / {getTotalQuestionsCount(tabs[activeTab]?.name)}
                            </span>
                          </div>
                          <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-blue-500 rounded-full transition-all duration-300 ease-in-out"
                              style={{ width: `${getProgressPercentage()}%` }}
                            ></div>
                          </div>
                        </div>
                        
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-3">Overall Progress</h4>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-gray-600">Total Assessment</span>
                            <span className="text-sm font-semibold text-emerald-600">
                              {getOverallProgress().completed} / {getOverallProgress().total}
                            </span>
                          </div>
                          <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-emerald-500 rounded-full transition-all duration-300 ease-in-out"
                              style={{ width: `${getOverallProgress().percentage}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>

                      {/* Compliance Charts */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Compliance Status</h4>
                        <div className="flex items-center space-x-6">
                          {maturityLevel && renderControlChart('ML1')}
                          {(maturityLevel === 'ML2' || maturityLevel === 'ML3') && renderControlChart('ML2')}
                          {maturityLevel === 'ML3' && renderControlChart('ML3')}
                        </div>
                      </div>

                      {/* Quick Actions */}
                      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                        <div className="text-sm text-gray-500">
                          Quick access to updates and resources
                        </div>
                        <div className="flex items-center space-x-2">
                          <UpdatesPopover />
                          <QuickLinks />
                        </div>
                      </div>
                    </div>
                  )}

                </div>
              </div>
            )}
          </div>

      </div>

      <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
        <div className="relative z-0">
          {maturityLevel ? (
            currentQuestions && currentQuestions.length > 0 ? (
              <ControlSection
                controlName={tabs[activeTab]?.name || ''}
                questions={currentQuestions}
                answers={answers}
                onAnswerChange={onAnswerChange}
                summaryRecommendations={summaryRecommendations}
                hideOverallProgress={true}
              />
            ) : (
              <div className="bg-white shadow rounded-lg p-4 text-center">
                <h3 className="text-lg font-medium text-gray-700 mb-2">Loading control questions...</h3>
                <p className="text-gray-500">
                  {`Maturity Level: ${maturityLevel}, Control: ${tabs[activeTab]?.name || 'Unknown'}`}
                </p>
                <button 
                  onClick={() => {
                    // Force refresh questions
                    if (typeof getQuestionsForML === 'function' && tabs && tabs[activeTab]) {
                      const controlName = tabs[activeTab].name;
                      console.log("Manually loading questions for:", controlName, maturityLevel);
                      
                      // Log what getQuestionsForML returns
                      const ml1Questions = getQuestionsForML(controlName, 'ML1');
                      console.log(`${controlName} ML1 questions:`, ml1Questions.length);
                      
                      // Force a refresh by toggling the maturity level
                      const currentML = maturityLevel;
                      setMaturityLevel('');
                      setTimeout(() => setMaturityLevel(currentML), 50);
                    }
                  }}
                  className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  Reload Questions
                </button>
              </div>
            )
          ) : (
            <div className="bg-white shadow rounded-lg p-8 text-center">
              <h2 className="text-2xl font-bold text-gray-700 mb-4">Essential Eight Assessment</h2>
              <p className="text-gray-600 mb-6">Please select a maturity level to begin your assessment.</p>
              <div className="flex justify-center space-x-4">
                {['ML1', 'ML2', 'ML3'].map(ml => (
                  <button
                    key={ml}
                    onClick={() => {
                      console.log(`ML button clicked: ${ml}`);
                      
                      // Use the enhanced createNewAssessment function to create a new assessment with this maturity level
                      if (typeof createNewAssessment === 'function') {
                        createNewAssessment(ml);
                      } else {
                        // Fallback if createNewAssessment is not available
                        console.error("createNewAssessment function not available");
                        setMaturityLevel(ml);
                        setActiveTab(0);
                      }
                    }}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                  >
                    {ml}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <ConfirmationDialog
          message={dialogMessage}
          onConfirm={handleConfirmAction}
          onCancel={() => setShowConfirmDialog(false)}
        />
      )}
    </div>
  );
};

export default E8AssessmentPanel;
